/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ITasksConfig, INotesConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { Redirect, RouteConfig } from 'aurelia-router';
import { ITaskBinding } from 'app/core/components/tasks/task';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { I18N } from 'aurelia-i18n';
import { TaskService } from 'app/core/services/task-service';
import { Toaster } from 'app/shared/lib/toaster';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class EditCaseTaskPage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    protected memberId!: string;
    protected caseId!: string;
    protected taskId!: string;
    protected taskBinding: ITaskBinding | null = null;

    @bindable.string({ changeHandler: 'titleReload' })
    protected taskDisplay: string | null = null;

    protected routeConfig: RouteConfig | null = null;

    protected tasksConfig: ITasksConfig = {
        primaryTargetType: 'CM.CaseTask',
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        backRouteName: 'cm.case.tasks',
        addTargetProperty: this.addCaseProperty.bind(this),

        smartTasksConfig: {
            moduleTaskCode: 'CM.Task',
            assessmentsConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                backRouteName: 'cm.case.assessments'
            },
            documentsConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                addTargetProperty: (document: CM.CaseDocument) => { document.case = { id: this.caseId }; }
            },
            notesConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                addTargetProperty: (note: CM.CaseNote) => { note.case = { id: this.caseId }; }
            },
            correspondenceConfig: {
                transitionalPageId: '3051',
                moduleCode: 'CM',
                serviceRootUrl: 'cm/cases',
                rollupRootUrl: 'cm/cases/rollup/correspondence'
            },
            contactLogConfig: {
                serviceRootUrl: 'cm/cases',
                targetType: 'CM.Case',
                rollupConfig: {
                    serviceRootUrl: 'cd/members',
                    targetType: 'CD.Member'
                }
            }
        },

        appointmentsConfig: {
            addTargetProperty: (appointment: CM.CaseTaskAppointment) => { appointment.task = { id: this.taskId }; }
        }
    };

    private notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/cases/tasks',
        targetType: 'CM.CaseTask',
        addTargetProperty: this.addTaskProperty.bind(this),
        excludeSystem: false
    };

    private queuesConfig: IQueuesConfig = {
        serviceRootUrl: 'cm/tasks'
    }


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly context: CasePageContext,
        private readonly i18n: I18N,
        private readonly service: TaskService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate({ caseId, taskId }: { caseId: string, taskId: string }): Promise<Redirect | true> {
        if(!(caseId && taskId)) {
            throw new Error('Required parameters [caseId] and/or [taskId] were not found');
        }

        try {
            let caseNumber = this.context.case?.number;
            if(!caseNumber) {
                // load case number for error message if not found on context
                const loadedCase = await this.context.loadCase(caseId, undefined, ['number']);
                caseNumber = loadedCase.number;
            }
            this.service.configure(this.tasksConfig);
            const response = await this.service.loadTask(taskId, undefined, ['case.id']);
            const task: CM.CaseTask = response.results[0];
            const cmCase = task.case;
            if(cmCase?.id !== caseId) {
                // reroute user if task does not belong to the case
                const messageOptions = {
                    what: this.i18n.tr('core:task.$'),
                    item: this.i18n.tr('cm:case.$').toLowerCase(),
                    number: caseNumber
                };
                const message = this.i18n.tr('ui:messages.errors.doesNotExistFor', messageOptions);
                this.toaster.error().message(message).show();
                return new Redirect('tasks');
            }
            return true;
        } catch(error) {
            throw error;
        }
    }

    protected async activate(params: { caseId: string, taskId: string, activeTab?: string, from?: string }, routeConfig: RouteConfig): Promise<void> {
        this.memberId = this.context?.case?.member?.id || '';
        this.caseId = params.caseId;
        this.taskId = params.taskId;
        this.tasksConfig.memberId = this.memberId;
        this.tasksConfig.backRouteName = params.from || undefined;
        await this.accountContext.load();
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');

        this.taskBinding = {
            targetId: this.caseId,
            relatedModels: { "CD.Member": this.memberId, "CM.Case": this.caseId },
            taskId: this.taskId,
            smartTaskTargetId: this.caseId,
            tasksConfig: this.tasksConfig,
            notesConfig: this.notesConfig,
            queuesConfig: this.queuesConfig,
            readonly: this.context.caseIsReadOnly(canEditClosedCase),
            canDeactivateNotes: true,
            showAppointments: true,
            showTaskQueues: true,
            canUpdateTaskDueDate: true,
            showDelegateToQueue: true,
            showDelegateToUser: true,
            canSaveTask: true,
            activeTab: params.activeTab,
            isDisabled: this.context.case.isClosed ?? false
        };

        this.routeConfig = routeConfig;
    }

    ////****************** Events ****************************************************////
    protected titleReload(): void {
        if(this.routeConfig?.navModel) {
            this.routeConfig.navModel.setTitle(this.i18n.tr('cm:routes.tasks.$title', { what: this.taskDisplay }));
        }
    }


    ////****************** Functions *************************************************////
    public addCaseProperty(task: CM.CaseTask): void {
        task.case = { id: this.caseId };
    }

    public addTaskProperty(model: CM.CaseTaskNote | CM.CaseTaskAppointment): void {
        model.task = { id: this.taskId };
    }
}
